import { Badge, Box, Button, FileButton, Flex, Paper, Text, TextInput, Title } from "@mantine/core";
import { IconLink, IconTrash, IconUpload, IconFileText, IconDownload, IconLock } from "@tabler/icons-react";
import { errorNotification } from "../../shared/utils/errorNotification";
import { useRef } from "react";

interface DocumentItemFormProps {
  type: "mandatory" | "additional";
  index?: number;
  data?: { name: string; file: File | null; url?: string }; // Local state for new/editable
  existingDoc?: any; // If present, shows "View/Delete" mode
  isViewMode?: boolean;
  isEditMode?: boolean;
  companyStatus?: string | null;
  title?: string;
  description?: string;
  fileOnly?: boolean; // If true, only show file upload (no URL option)
  downloadTemplateUrl?: string; // If provided, shows a download template button
  disabled?: boolean; // If true, disables upload (mutual exclusivity with another doc)
  disabledMessage?: string; // Message shown when disabled

  // Actions
  onChange?: (field: string, value: any) => void;
  onRemove?: () => void; // Remove local form
  onDelete?: () => Promise<void> | void; // Delete existing doc
}

const DocumentItemForm = ({
  type,
  index,
  data,
  existingDoc,
  isViewMode = false,
  companyStatus,
  onChange,
  onRemove,
  onDelete,
  title: customTitle,
  description: customDescription,
  fileOnly = false,
  downloadTemplateUrl,
  disabled = false,
  disabledMessage = "Remove the other document to select this one instead.",
}: DocumentItemFormProps) => {
  const resetRef = useRef<() => void>(null);

  const isMandatory = type === "mandatory";
  const canEditMandatory = companyStatus === "ACTION_REQUIRED";
  const title =
    customTitle ||
    (isMandatory
      ? "Business Registration Document"
      : existingDoc
        ? existingDoc.name || existingDoc.filename
        : `Additional Documents ${typeof index === "number" ? index + 1 : ""}`);

  const description =
    customDescription || (isMandatory ? "Please upload your business registration document" : "Documents / Portfolio");

  // Render Existing Document View
  if (existingDoc) {
    return (
      <Paper
        shadow="none"
        p="lg"
        radius="md"
        withBorder
        mb={isMandatory ? "xl" : "lg"}
        style={{ borderColor: "#E5E7EB" }}
      >
        <Flex justify="space-between" align="flex-start" mb="md">
          <Box>
            <Title order={5} mb={4} fw={400}>
              {title}
            </Title>
            {isMandatory && (
              <Text fz="sm" c="slate.6">
                {description}
              </Text>
            )}
          </Box>
          {isMandatory && (
            <Badge color="red" variant="light" size="md" radius="xl" px={8} py={4}>
              MANDATORY
            </Badge>
          )}
          {!isMandatory && !isViewMode && (
            <Button variant="subtle" color="red" size="sm" onClick={onDelete}>
              Delete
            </Button>
          )}
        </Flex>

        {isMandatory ? (
          // Mandatory View when populated
          <Paper withBorder p="lg" radius="md" style={{ borderColor: "#E5E7EB" }}>
            <Flex justify="space-between" align="center">
              <Flex align="center" gap="sm">
                <IconFileText size={30} color="#2563EB" />
                <Box>
                  <Text fw={500} size="sm">
                    {existingDoc.name || existingDoc.filename}
                  </Text>
                  {existingDoc.createdAt && (
                    <Text size="xs" c="slate.6" mt={2}>
                      Uploaded{" "}
                      {new Date(existingDoc.createdAt).toLocaleDateString("en-GB", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      })}
                    </Text>
                  )}
                </Box>
              </Flex>
              {!isViewMode && (!isMandatory || (isMandatory && canEditMandatory)) && (
                <Button variant="subtle" color="red" size="sm" leftSection={<IconTrash size={16} />} onClick={onDelete}>
                  Remove
                </Button>
              )}
            </Flex>
          </Paper>
        ) : (
          // Additional View when populated
          <Paper withBorder p="lg" radius="md" style={{ borderColor: "#E5E7EB" }}>
            <Flex align="center" gap="sm">
              <IconFileText size={30} color="#2563EB" />
              <Box>
                <Text fw={500} size="sm">
                  {existingDoc.name || existingDoc.filename}
                </Text>
                {existingDoc.createdAt && (
                  <Text size="xs" c="slate.6" mt={2}>
                    Uploaded{" "}
                    {new Date(existingDoc.createdAt).toLocaleDateString("en-GB", {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                    })}
                  </Text>
                )}
              </Box>
            </Flex>
          </Paper>
        )}
      </Paper>
    );
  }

  // Render Disabled State (mutual exclusivity)
  if (disabled) {
    return (
      <Paper
        shadow="none"
        p="lg"
        radius="md"
        withBorder
        mb={isMandatory ? "xl" : "lg"}
        style={{ borderColor: "#E5E7EB", opacity: 0.5 }}
      >
        <Flex justify="space-between" align="flex-start" mb="md">
          <Box>
            <Title order={5} mb={4} fw={400}>
              {title}
            </Title>
            <Text fz="sm" c="slate.6">
              {description}
            </Text>
          </Box>
          <Badge color="red" variant="light" size="md" radius="xl" px={8} py={4}>
            MANDATORY
          </Badge>
        </Flex>
        <Paper
          shadow="none"
          withBorder
          p="xl"
          radius="md"
          style={{
            borderStyle: "dashed",
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 120,
            backgroundColor: "#F9FAFB",
          }}
        >
          <Box bg="gray.1" p="sm" style={{ borderRadius: "50%", display: "inline-flex" }} mb="sm">
            <IconLock size={24} color="#9CA3AF" />
          </Box>
          <Text size="sm" fw={500} c="slate.5" mb={4}>
            Not Available
          </Text>
          <Text size="xs" c="slate.4">
            {disabledMessage}
          </Text>
        </Paper>
      </Paper>
    );
  }

  const handleFileSelected = (file: File | null) => {
    // Check logic for 10MB limit
    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
    if (file && file.size > MAX_FILE_SIZE) {
      errorNotification({
        title: "File too large",
        message: "The file size should not exceed 10MB.",
      });
      resetRef.current?.();
      return;
    }

    onChange?.("file", file);
    // Clear URL when file is selected (if URL option is available)
    if (file && !fileOnly) {
      onChange?.("url", "");
    }
  };

  // Render Edit/Upload Form
  return (
    <Paper
      shadow="none"
      p="lg"
      radius="md"
      withBorder
      mb={isMandatory ? "xl" : "lg"}
      style={{ borderColor: "#E5E7EB" }}
    >
      <Flex justify="space-between" align="center" mb="md">
        <Box flex={1}>
          <Title order={5} mb={4} fw={400}>
            {title}
          </Title>
          {isMandatory && (
            <Text fz="sm" c="slate.6">
              {description}
            </Text>
          )}
        </Box>
        {isMandatory ? (
          <Badge color="red" variant="light" size="md" radius="xl" px={8} py={4}>
            MANDATORY
          </Badge>
        ) : (
          !isViewMode && (
            <Flex gap="sm" align="center">
              <Button variant="subtle" color="red" size="sm" onClick={onRemove}>
                Remove
              </Button>
            </Flex>
          )
        )}
      </Flex>

      {!isMandatory && (
        <TextInput
          label="Document Name"
          placeholder="e.g., ISO Certificate"
          required
          mb="md"
          withAsterisk
          value={data?.name || ""}
          onChange={(e) => onChange?.("name", e.target.value)}
          readOnly={isViewMode}
        />
      )}

      {!isMandatory && (
        <Text fz="sm" fw={400} mb="xs">
          Documents / Portfolio
        </Text>
      )}

      {/* Download Template Button */}
      {downloadTemplateUrl && (
        <Paper
          shadow="none"
          withBorder
          p="md"
          radius="md"
          mb="md"
          style={{ borderColor: "#3B82F6", backgroundColor: "#EFF6FF" }}
        >
          <Flex align="center" gap="md">
            <Box
              bg="primary.6"
              p="sm"
              style={{ borderRadius: "50%", display: "inline-flex" }}
            >
              <IconDownload size={20} color="white" />
            </Box>
            <Box flex={1}>
              <Text fw={500} size="sm" mb={2}>
                Download Authority Letter Template
              </Text>
              <Text size="xs" c="slate.6">
                Download, sign, and upload the signed authority letter
              </Text>
            </Box>
            <Button
              component="a"
              href={downloadTemplateUrl}
              target="_blank"
              rel="noopener noreferrer"
              leftSection={<IconDownload size={16} />}
              variant="filled"
              color="primary"
              size="sm"
            >
              Download
            </Button>
          </Flex>
        </Paper>
      )}

      {/* Upload and URL Fields (Vertical Layout) */}
      <Box>
        {/* Upload File Field */}
        <Box mb="md">
          <Text fz="sm" fw={500} mb="xs">
            <IconUpload size={16} style={{ verticalAlign: "middle", marginRight: 4 }} color="#2563EB" />
            Upload File
          </Text>
          {!isViewMode ? (
            <>
              {data?.file ? (
                <Paper
                  shadow="none"
                  withBorder
                  p="lg"
                  radius="md"
                  style={{
                    borderColor: "#2563EB1A",
                  }}
                >
                  <Flex justify="space-between" align="center">
                    <Flex align="center" gap="sm">
                      <IconUpload size={30} color="#2563EB" />
                      <Box>
                        <Text fw={500} size="sm">
                          {data.file.name}
                        </Text>
                        {isMandatory && (
                          <Text size="xs" c="slate.6" mt={2} fw={400}>
                            {customTitle || "Business Registration Document"}
                          </Text>
                        )}
                      </Box>
                    </Flex>
                    <Flex gap="sm">
                      <Button
                        variant="subtle"
                        color="red"
                        size="sm"
                        leftSection={<IconTrash size={16} />}
                        onClick={() => {
                          onChange?.("file", null);
                        }}
                      >
                        Remove
                      </Button>
                    </Flex>
                  </Flex>
                </Paper>
              ) : (
                <FileButton
                  resetRef={resetRef}
                  onChange={handleFileSelected}
                  accept="application/pdf,image/png,image/jpeg"
                  disabled={!fileOnly && !!(data?.url && data.url.trim())}
                >
                  {(props) => (
                    <Paper
                      shadow="none"
                      withBorder
                      p="xl"
                      radius="md"
                      onDragOver={(event) => {
                        event.preventDefault();
                      }}
                      onDrop={(event) => {
                        event.preventDefault();
                        if (!fileOnly && data?.url && data.url.trim()) return;
                        const [file] = Array.from(event.dataTransfer.files || []);
                        if (!file) return;
                        handleFileSelected(file);
                      }}
                      style={{
                        borderStyle: "solid",
                        textAlign: "center",
                        cursor: !fileOnly && data?.url ? "not-allowed" : "pointer",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        minHeight: 120,
                        opacity: !fileOnly && data?.url ? 0.5 : 1,
                      }}
                      {...props}
                    >
                      <Box bg="blue.0" p="sm" style={{ borderRadius: "50%", display: "inline-flex" }} mb="sm">
                        <IconUpload size={30} color="#2563EB" />
                      </Box>
                      <Text size="sm" fw={500} mb={4}>
                        Click to upload or drag and drop
                      </Text>
                      <Text size="xs" c="slate.6">
                        PNG, JPG, or PDF • Max 10MB
                      </Text>
                    </Paper>
                  )}
                </FileButton>
              )}
            </>
          ) : (
            <Paper shadow="none" withBorder p="md" radius="md">
              {data?.file ? <Text>File uploaded: {data.file.name}</Text> : <Text c="slate.6">No file uploaded</Text>}
            </Paper>
          )}
        </Box>

        {/* OR Divider - Only show if URL option is available */}
        {!fileOnly && (
          <Flex align="center" gap="md" mb="md">
            <Box style={{ flex: 1, height: 1, backgroundColor: "#dee2e6" }} />
            <Text size="sm" c="slate.6" fw={500}>
              OR
            </Text>
            <Box style={{ flex: 1, height: 1, backgroundColor: "#dee2e6" }} />
          </Flex>
        )}

        {/* Public URL Field - Only show if URL option is available */}
        {!fileOnly && (
          <Box>
            <Text fz="sm" fw={500} mb="xs">
              <IconLink size={16} style={{ verticalAlign: "middle", marginRight: 4 }} />
              Public URL
            </Text>
            <TextInput
              placeholder="https://example.com/document.pdf"
              value={data?.url || ""}
              onChange={(e) => {
                onChange?.("url", e.target.value);
                // Clear file when URL is entered
                if (e.target.value.trim()) {
                  onChange?.("file", null);
                }
              }}
              readOnly={isViewMode}
              disabled={!!data?.file}
              leftSection={<IconLink size={16} />}
            />
            {data?.file && (
              <Text size="xs" c="slate.6" mt={4}>
                Remove the uploaded file to use a URL instead
              </Text>
            )}
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default DocumentItemForm;
